import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { arrayHasItems } from '../../helpers/common';
import {
  DemandPlanningLoadingKeys,
  getBundleReplacementShiftTableData,
} from '../../slices/demandPlanningSlice';
import { LoaderSpinner } from '../LoaderSpinner';
import { BundleReplacementShiftSummaryBar } from './bundleReplacementShiftComponents/BundleReplacementShiftSummaryBar';
import { BundleReplacementShiftReplacementRail } from './bundleReplacementShiftComponents/BundleReplacementShiftReplacementRail';
import { BundleReplacementShiftMainTable } from './bundleReplacementShiftComponents/BundleReplacementShiftMainTable';
import {
  buildBundleReplacementGroups,
  filterBundleReplacementRows,
  summarizeBundleReplacementRows,
} from '../../lib/bundleReplacementShiftHelpers';

const BundleReplacementShiftTable = ({
  selectedForecastDemandPlanning,
  searchTerm,
  selectedCategoryId,
  selectedSubcategoryId,
  selectedStatusId,
}) => {
  const dispatch = useDispatch();
  const { bundleReplacementShiftTableData, loadingKeys } = useSelector(state => state.demandPlanning);

  const [localSearch, setLocalSearch] = useState('');
  const [exceptionsOnly, setExceptionsOnly] = useState(false);

  const bundleReplacementShiftTableLoading = loadingKeys.includes(
    DemandPlanningLoadingKeys.getBundleReplacementShiftTableData
  );

  const rows = useMemo(() => {
    const raw = bundleReplacementShiftTableData?.rows;
    return arrayHasItems(raw) ? raw : [];
  }, [bundleReplacementShiftTableData]);

  const filteredRows = useMemo(
    () => filterBundleReplacementRows(rows, { search: localSearch, exceptionsOnly }),
    [rows, localSearch, exceptionsOnly]
  );

  const summary = useMemo(() => summarizeBundleReplacementRows(filteredRows), [filteredRows]);
  const replacementGroups = useMemo(() => buildBundleReplacementGroups(filteredRows), [filteredRows]);

  const forecastYear =
    bundleReplacementShiftTableData?.forecastYear ??
    selectedForecastDemandPlanning?.forecast_year ??
    selectedForecastDemandPlanning?.forecastYear;

  useEffect(() => {
    const demandPlanningId = selectedForecastDemandPlanning?.id;
    const demandPlanningYear =
      selectedForecastDemandPlanning?.forecast_year ??
      selectedForecastDemandPlanning?.forecastYear;

    if (demandPlanningId == null || demandPlanningYear == null || demandPlanningYear === '') return;

    dispatch(
      getBundleReplacementShiftTableData({
        demandPlanningId,
        demandPlanningYear,
        searchTerm,
        selectedCategoryId,
        selectedSubcategoryId,
        selectedStatusId,
      })
    );
  }, [
    dispatch,
    selectedForecastDemandPlanning?.id,
    selectedForecastDemandPlanning?.forecast_year,
    selectedForecastDemandPlanning?.forecastYear,
    searchTerm,
    selectedCategoryId,
    selectedSubcategoryId,
    selectedStatusId,
  ]);

  if (bundleReplacementShiftTableLoading) {
    return (
      <section className="flex-1 px-4 py-4 lg:px-6">
        <div className="flex justify-center items-center h-full mt-8">
          <LoaderSpinner width="80" height="80" border="5" colorSpinner="gray" />
        </div>
      </section>
    );
  }

  return (
    <section className="mb-4 rounded-lg border border-gray-200 bg-white p-2 shadow-sm lg:p-4">
      <div className="flex flex-col gap-3 border-b border-gray-200 pb-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <span className="mb-2 inline-flex rounded-md border border-gray-200 bg-white px-2 py-1 text-[11px] font-medium text-gray-900">
            Bundle Replacement Shift
          </span>
          <h2 className="text-xl font-semibold tracking-[-0.02em] text-gray-900">
            Verify replacement demand applied inside bundle-generated component demand
          </h2>
          <p className="mt-1 max-w-3xl text-sm text-gray-500">
            This layer shows where a discontinued component still appears inside a bundle BOM and how that
            bundle-driven demand is redirected to the designated replacement SKU.
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <input
            className="h-9 w-full min-w-[220px] rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-900 sm:w-[280px]"
            onChange={event => setLocalSearch(event.target.value)}
            placeholder="Search bundle, component, or replacement SKU"
            value={localSearch}
          />
          <label className="flex h-9 items-center gap-2 rounded-md border border-gray-300 bg-white px-3 text-xs font-medium text-gray-900">
            <input
              checked={exceptionsOnly}
              className="size-3.5 rounded border-gray-300"
              onChange={event => setExceptionsOnly(event.target.checked)}
              type="checkbox"
            />
            Exceptions only
          </label>
        </div>
      </div>

      <BundleReplacementShiftSummaryBar summary={summary} />

      <div className="mt-4 grid gap-4 xl:grid-cols-[minmax(260px,0.82fr)_minmax(0,1.7fr)]">
        <BundleReplacementShiftReplacementRail replacementGroups={replacementGroups} />
        <BundleReplacementShiftMainTable rows={filteredRows} />
      </div>
    </section>
  );
};

export default BundleReplacementShiftTable;
